import { Icon, Tooltip } from "@saviynt/design-system";
import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

export function CopyButton({ value }: { value: string }) {
  const intl = useIntl();
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    [],
  );

  const copyLabel = intl.formatMessage({
    id: "RWGXI9",
    defaultMessage: "Copy tenant name",
    description:
      "Aria label and tooltip for the button that copies the current tenant name",
  });
  const copiedLabel = intl.formatMessage({
    id: "rvTg2I",
    defaultMessage: "Copied",
    description:
      "Confirmation shown after the tenant name is copied to the clipboard",
  });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard write can fail in insecure contexts or when permission is denied.
    }
  };

  const button = (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={copied ? copiedLabel : copyLabel}
      data-testid="shell-header-currentTenant-copy"
      className="up-icon flex shrink-0 items-center justify-center w-6 h-6 rounded-md
        hover:bg-[var(--color-background-neutral-subtle,rgba(55,81,255,0.06))]
        transition-colors duration-100 outline-none
        focus-visible:ring-2 focus-visible:ring-[var(--color-border-focus,#3751ff)]"
    >
      <Icon kind={copied ? "Check" : "Copy"} size="small" />
    </button>
  );

  return (
    <Tooltip
      trigger={button}
      text={copied ? copiedLabel : copyLabel}
      placement="top"
    />
  );
}
